import React, { useMemo, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSort, faSortDown, faSortUp } from '@fortawesome/free-solid-svg-icons';
import PropTypes from 'prop-types';

import DropdownRequest from '../../../../components/Dropdown/Request';

import { dropdownStatusFormatted, generateTableHead, getSelectedMethods } from '../../helpers';

const Head = ({ config, sort, filters, fetchTableFilters, onSortTable, onSelectMethods }) => {
  const { t } = useTranslation();
  const { darkMode } = useSelector((state) => state.general);

  const selectedMethods = useMemo(
    () => getSelectedMethods(config?.methods, filters.method),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [config, filters.method],
  );
  const headers = useMemo(() => generateTableHead(config?.access, sort?.columns), [config, sort]);
  const headerFilters = useMemo(
    () => ({
      modes: config?.modes?.map((mode) => ({ ...mode, title: `${mode.title} ${mode.count ? `(${mode.count})` : ''}` })),
      statuses: config?.statuses?.map((status) => ({
        ...status,
        title: `${status.title} ${status.count || status.count === 0 ? `(${status.count})` : ''}`,
      })),
      fraud_risk: config?.fraud_risk?.map((risk) => ({
        ...risk,
        id: risk.code,
        title: `${risk.title} ${risk.count || risk.count === 0 ? `(${risk.count})` : ''}`,
      })),
      methods: config?.methods?.map((method) => ({
        ...method,
        title: `${method.title} ${method.count ? `(${method.count})` : ''}`,
        label: method.title,
        active: method.visibility,
      })),
    }),
    [config],
  );

  const getSortIcon = useCallback(
    (head) => {
      if (head?.active === 'desc') {
        return faSortDown;
      }
      if (head?.active === 'asc') {
        return faSortUp;
      }
      return faSort;
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [headers],
  );

  const sendSortValue = (e, item) => {
    e.preventDefault();
    onSortTable(item);
  };

  return (
    <thead>
      <tr>
        {headers.map((head) => {
          switch (head.title) {
          case 'Method':
            return (
              <th key={head.title} className="tickets__status" {...head.attrs}>
                <DropdownRequest
                  type="select"
                  title={t('Table.Method')}
                  items={headerFilters?.methods}
                  queryRequest="method"
                  value={selectedMethods}
                  onRequestFunc={() => fetchTableFilters('methods')}
                  onChangeSelect={onSelectMethods}
                  clearData={() => true}
                  withServicesId={false}
                  darkMode={darkMode}
                  isSorted
                />
              </th>
            );

          case 'Status':
            return (
              <th key={head.title} className="tickets__status" {...head.attrs}>
                <DropdownRequest
                  title={t('Table.Status')}
                  items={dropdownStatusFormatted(headerFilters?.statuses)}
                  queryRequest="status"
                  onRequestFunc={() => fetchTableFilters('statuses')}
                  clearData={() => true}
                  darkMode={darkMode}
                />
              </th>
            );

          case 'Fraud risk':
            return (
              <th key={head.title} className="tickets__status" {...head.attrs}>
                <DropdownRequest
                  title={t('Table.Fraud risk')}
                  items={headerFilters?.fraud_risk}
                  queryRequest="fraud_risk"
                  onRequestFunc={() => fetchTableFilters('fraud_risk')}
                  clearData={() => true}
                  darkMode={darkMode}
                />
              </th>
            );

          case 'Mode':
            return (
              <th key={head.title} className="tickets__status table__orders-mode" {...head.attrs}>
                <DropdownRequest
                  title={t('Table.Mode')}
                  items={headerFilters?.modes}
                  queryRequest="mode"
                  onRequestFunc={() => fetchTableFilters('modes')}
                  clearData={() => true}
                  darkMode={darkMode}
                  pullRight
                />
              </th>
            );

          default:
            if (head?.name) {
              return (
                <th key={head.title} className={head.class} {...head.attrs}>
                  <a href="# " onClick={(e) => sendSortValue(e, head)}>
                    {head?.title}
                    {' '}
                    <FontAwesomeIcon icon={getSortIcon(head)} />
                  </a>
                </th>
              );
            }
            return (
              <th key={head.title} className={head.class} {...head.attrs}>
                {head.title}
              </th>
            );
          }
        })}
      </tr>
    </thead>
  );
};

Head.propTypes = {
  config: PropTypes.object,
  sort: PropTypes.object,
  filters: PropTypes.object,
  fetchTableFilters: PropTypes.func,
  onSortTable: PropTypes.func,
  onSelectMethods: PropTypes.func,
};

export default Head;
